<template>
  <div>
    <b-container class="my-2">
      <b-row>
        <b-col>
          <b-collapse
            class="pt-2"
            :visible="status.showHelp"
            id="collapse-help"
          >
            <b-alert show variant="warning">
              <div>
                <h4 class="font-weight-bold">Welcome👋️</h4>
                <!-- <p class="font-weight-bold">Let's get started!</p> -->
                <hr />
                <p>
                  ECCE helps you to analyze your documents and corpora by
                  detecting named entities to extracting an entity network. You
                  can add documents one by one or by uploading plain text in a
                  CSV file. You can access your corpus later or on another
                  device using the unique corpus ID you find at the top. We
                  deliberately rely on this loginless scheme to allow easy yet
                  secure data sharing between users. Currently, only English
                  texts are supported.
                </p>
                <span class="font-weight-bold">First Steps:</span>
                <ol>
                  <li>Create an empty corpus or explore an example.</li>
                  <li>Add documents manually or upload a CSV file.</li>
                  <li>
                    Advance by clicking on
                    <b-link @click="goToExtractEntities()" variant="primary"
                      >Annotate Documents
                      <b-icon
                        font-scale="1.0"
                        icon="link45deg"
                      ></b-icon></b-link
                    >.
                  </li>
                </ol>
              </div>
              <b-button class="my-2" variant="primary" @click="closeHelp()"
                >Close Help</b-button
              >
            </b-alert>
          </b-collapse>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="5" class="py-2 my-auto">
          <b-button
            v-b-tooltip.hover
            title="Add a new document."
            size="md"
            v-b-modal.modal-add
            :disabled="!validateCorpusId(corpus_id)"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="plus"></b-icon>
            Add Document
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="md"
            title="Upload multiple documents."
            v-on:click="goToManageDocumentsUpload()"
            :disabled="!validateCorpusId(corpus_id)"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="cloud-upload"></b-icon>
            Upload CSV
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="md"
            title="Refresh the document list."
            v-on:click="updateDocumentList(corpus_id)"
            :disabled="!validateCorpusId(corpus_id)"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="arrow-repeat"></b-icon>
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="md"
            title="Help"
            @click="toogleHelp()"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="question-circle"></b-icon>
          </b-button>
        </b-col>
        <b-col md="3" class="py-2 my-auto">
          <h5 class="text-muted my-1" align="left">
            Documents:
            <b-badge variant="primary">{{ pagination.documents }}</b-badge>
          </h5>
        </b-col>
        <b-col md="4" class="py-2 my-auto">
          <b-button
            v-on:click="goToExtractEntities()"
            v-b-tooltip.hover
            size="md"
            title="Detect named entities in your corpus."
            :disabled="!validateCorpusId(corpus_id)"
            variant="primary"
            class="shadow my-1"
          >
            Annotate Documents
            <b-icon icon="chevron-compact-right"></b-icon>
          </b-button>
        </b-col>
      </b-row>

      <!-- Conditional Rendering of Document List-->
      <b-overlay :show="status.showSpinner" rounded="sm">
        <!-- First Option: Documents in Corpus -->
        <b-card-group deck v-if="documents.length > 0">
          <b-card
            class="my-2"
            v-for="(document, id) in documents"
            :key="id"
            style="min-width: 350px"
          >
            <p class="card-text" style="height: 400px; overflow: hidden">
              {{
                document.text.length >= 800
                  ? document.text.substring(0, 800) + " ... "
                  : document.text
              }}
            </p>
            <div slot="footer">
              <b-link variant="primary" v-on:click="openModalEdit(id)" block
                >Edit</b-link
              >
              <b-link variant="primary" v-on:click="deleteDocument(id)" block
                >Delete</b-link
              >
              <span class="text-muted"> - {{ document._id["$oid"] }}</span>
            </div>
          </b-card>
        </b-card-group>

        <!-- Second Option: No Documents in Corpus -->
        <b-row v-else>
          <b-col cols="12">
            <b-overlay show rounded="sm" style="height: 75vh">
              <template #overlay>
                <div class="text-center">
                  <b-icon
                    class="my-2"
                    icon="exclamation-circle"
                    font-scale="3"
                    variant="secondary"
                  ></b-icon>
                  <h3 class="text-muted text-center">
                    Documents will be displayed here
                  </h3>
                  <p class="text-muted text-center">
                    <mark>Add</mark> new Documents or start fresh with a
                    <mark>new Corpus</mark>.
                  </p>
                </div>
              </template>
            </b-overlay>
          </b-col>
        </b-row>
      </b-overlay>

      <!-- use shared component to add a b-row containing pagination -->
      <pagination :pagination="pagination"></pagination>
    </b-container>

    <!-- Modals -->

    <b-modal
      id="modal-add"
      ref="modal-add"
      title="New Document"
      size="xl"
      scrollable
      no-fade
      @ok="addDocument"
    >
      <b-form-textarea
        v-model="modals.add.text"
        placeholder="Lots of Space for your Document!"
        rows="20"
        no-resize
      >
      </b-form-textarea>
    </b-modal>

    <b-modal
      id="modal-edit"
      ref="modal-edit"
      title="Edit Document"
      size="xl"
      scrollable
      no-fade
      @ok="editDocument"
    >
      <b-form-textarea
        v-model="modals.edit.text"
        placeholder="Lots of Space for your Document!"
        rows="20"
        no-resize
      >
      </b-form-textarea>
    </b-modal>
  </div>
</template>

<script>
import pagination from "./shared/pagination.vue";
import requestsMixin from "../mixins/requestsMixin";
import messagesMixin from "../mixins/messagesMixin";

export default {
  components: { pagination },
  name: "component-manage-documents",
  props: { corpus_id: { type: String } },
  mixins: [requestsMixin, messagesMixin],
  data() {
    return {
      // Variables used to controle
      // pagination element
      pagination: {
        currentPage: 1,
        documents: 0,
        perPage: 9,
      },

      // Variables needed for
      // modals used to add and
      // edit documents
      modals: {
        add: {
          text: "",
        },
        edit: {
          text: "",
          localId: -1,
          documentId: "",
        },
      },

      // Variables used for tracking
      // the state of the component
      status: {
        showSpinner: false,
        showHelp: false,
      },
      documents: [],
    };
  },
  methods: {
    /* ----------------------- Navigating the Application ----------------------- */

    // Validate corpus and advance to
    // next step which is annotating the documents
    goToExtractEntities() {
      if (this.documents.length > 0) {
        this.$router.push({
          name: "extract_entities",
          params: { reparseDocuments: true },
        });
      } else {
        this.showToast(
          "No Documents in Corpus",
          "Please add at least one document to the corpus to advance.",
          "danger"
        );
      }
    },

    // Navigate to the csv upload component
    goToManageDocumentsUpload() {
      this.$router.push({
        name: "manage_documents_upload",
      });
    },

    /* --------------------------------- Modals --------------------------------- */

    // Show Modal for editing text of a document
    // and store index, document_id and the text
    // in temp variables as long as modal open
    openModalEdit(id) {
      this.modals.edit.localId = id;
      this.modals.edit.documentId = this.documents[id]["_id"]["$oid"];
      this.modals.edit.text = this.documents[id]["text"];
      this.$refs["modal-edit"].show();
    },

    // Add document and refresh document list
    addDocument() {
      new Promise((resolve) => {
        this.status.showSpinner = true; // Show spinner
        resolve(true);
      })
        .then(() => {
          return this.addDocumentRemote(this.corpus_id, this.modals.add.text);
        })
        .then(() => {
          this.modals.add.text = "";
          this.pagination.currentPage = 1;
        })
        // Fetch meta information about corpus
        .then(() => {
          return this.fetchCorpusMetaData(this.corpus_id);
        })
        .then((document_count) => {
          this.pagination.documents = document_count;
        })
        // Fetch documents in corpus
        .then(() => {
          return this.fetchCorpus(
            this.corpus_id,
            this.pagination.currentPage,
            this.pagination.perPage
          );
        })
        .then((response) => {
          this.documents = response;
          this.showToast(
            "Document added",
            "Document was added to the corpus.",
            "success"
          );
        })
        // Stop spinner
        .finally(() => {
          this.status.showSpinner = false; // Hide spinner
        })
        // Shwow error message
        .catch((error) => {
          console.log(error);
          this.showToast(String(error.message), String(error.cause), "danger");
        });
    },

    // Edit document and refresh document list
    editDocument() {
      new Promise((resolve) => {
        this.status.showSpinner = true; // Show spinner
        resolve(true);
      })
        .then(() => {
          return this.editDocumentRemote(
            this.corpus_id,
            this.modals.edit.documentId,
            this.modals.edit.text
          );
        })
        .then(() => {
          let id = this.modals.edit.localId;
          this.documents[id]["text"] = this.modals.edit.text;
          this.$refs["modal-edit"].hide();
          this.modals.edit.text = "";
        })
        .then(() => {
          this.showToast(
            "Document updated",
            "Document text was successfully updated.",
            "success"
          );
        })
        // Stop spinner
        .finally(() => {
          this.status.showSpinner = false; // Hide spinner
        })
        // Shwow error message
        .catch((error) => {
          console.log(error);
          this.showToast(String(error.message), String(error.cause), "danger");
        });
    },

    // Delete document and refresh document list
    deleteDocument(id) {
      new Promise((resolve) => {
        this.status.showSpinner = true; // Show spinner
        resolve(true);
      })
        .then(() => {
          return this.deleteDocumentRemote(
            this.corpus_id,
            this.documents[id]["_id"]["$oid"]
          );
        })
        // Fetch meta information about corpus
        .then(() => {
          return this.fetchCorpusMetaData(this.corpus_id);
        })
        .then((document_count) => {
          this.pagination.documents = document_count;
        })
        // Fetch documents in corpus
        .then(() => {
          return this.fetchCorpus(
            this.corpus_id,
            this.pagination.currentPage,
            this.pagination.perPage
          );
        })
        .then((response) => {
          this.documents = response;
          this.showToast(
            "Document deleted",
            "Document was removed from the current corpus.",
            "success"
          );
        })
        // Stop spinner
        .finally(() => {
          this.status.showSpinner = false; // Hide spinner
        })
        // Shwow error message
        .catch((error) => {
          console.log(error);
          this.showToast(String(error.message), String(error.cause), "danger");
        });
    },

    /* -------------------------- Refresh Document List ------------------------- */
    // Fetch document subset according to the selected page.

    updateDocumentList(corpus_id) {
      if (!!corpus_id) {
        // Start spinner
        new Promise((resolve) => {
          this.status.showSpinner = true; // Show spinner
          resolve(true);
        })
          // Fetch meta information about corpus
          .then(() => {
            this.fetchCorpusMetaData(corpus_id).then((result_data) => {
              this.pagination.documents = result_data;
            });
          })
          // Fetch documents in corpus
          .then(() => {
            return this.fetchCorpus(
              corpus_id,
              this.pagination.currentPage,
              this.pagination.perPage
            );
          })
          .then((response) => {
            this.documents = response;
          })
          // Stop spinner
          .finally(() => {
            this.status.showSpinner = false; // Hide spinner
          })
          // Shwow error message
          .catch((error) => {
            console.log(error);
            this.showToast(
              String(error.message),
              String(error.cause),
              "danger"
            );
          });
      }
    },

    /* ---------------------------------- Help ---------------------------------- */

    toogleHelp() {
      this.status.showHelp = !this.status.showHelp;
      this.$cookies.set(
        "manageDocuments.showHelp",
        this.status.showHelp === true ? 1 : 0,
        Infinity
      );
    },

    openHelp() {
      this.status.showHelp = true;
      this.$cookies.set("manageDocuments.showHelp", 1, Infinity);
    },

    closeHelp() {
      this.status.showHelp = false;
      this.$cookies.set("manageDocuments.showHelp", 0, Infinity);
    },
  },
  watch: {
    corpus_id(value) {
      this.updateDocumentList(value);
    },
    "pagination.currentPage"(value) {
      this.updateDocumentList(this.corpus_id);
    },
  },
  created() {
    this.updateDocumentList(this.corpus_id);

    /* ---------------------- Determine help window status ---------------------- */

    // Get previous state of the help window
    var showHelpPrevious = this.$cookies.get("manageDocuments.showHelp");

    // Previous help window status is "open"
    if (showHelpPrevious == 1) {
      this.openHelp();
    }

    // No cooky set (first visit or disabled)
    if (showHelpPrevious === null && this.documents.length == 0) {
      this.openHelp();
    }
  },
};
</script scoped>

<style>
</style>


// WEBPACK FOOTER //
// src/components/manageDocuments.vue